import { useMemo, useState } from 'react';

import { useLocation, useOutletContext, useRouteLoaderData, useSearchParams } from '@remix-run/react';

import type { OutletContext } from '~/interfaces/outlet_context';
import type { AnyPaywall } from '~/interfaces/paywall';
import type { Post, PostCommunityComments } from '~/interfaces/post';
import type { PostTemplate } from '~/interfaces/post_template';
import type { Site } from '~/interfaces/site';
import type { Subscription } from '~/interfaces/subscriptions';
import type { RootLoaderData } from '~/root';

import { PostPageContext } from './PostPageContext';

// props from PostMetaInformation
// 'hide_premium_post' | 'paywall_shown' | 'force_premium' | 'enforce_paywall_breaks' | 'contains_subscriber_break'
interface PostPageProviderProps {
  site: Site;
  post: Post | PostTemplate;
  hide_premium_post?: boolean;
  paywall_shown?: boolean;
  metered_usage_shown?: boolean;
  force_premium?: boolean;
  enforce_paywall_breaks?: boolean;
  contains_subscriber_break?: boolean;
  children: React.ReactNode;
  subscription?: Subscription;
  previewSubscription?: Subscription;
  commentsEnabled: boolean;
  isDraft: boolean;
  onlyRenderComments: boolean;
  paywall: AnyPaywall | null;
  community_comments?: PostCommunityComments | null;
  meta_information_fetched?: boolean;
}

export const PostPageProvider: React.FC<PostPageProviderProps> = ({
  site,
  post,
  hide_premium_post,
  paywall_shown,
  metered_usage_shown,
  force_premium,
  enforce_paywall_breaks,
  contains_subscriber_break,
  subscription,
  previewSubscription,
  commentsEnabled,
  isDraft,
  onlyRenderComments,
  paywall,
  community_comments = null,
  meta_information_fetched,
  children,
}) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isPreviewModal = searchParams.get('preview_modal') === 'true';
  // override meta information if they're provided as a prop
  hide_premium_post = hide_premium_post ?? post.hide_premium_post;
  paywall_shown = paywall_shown ?? post.paywall_shown;
  force_premium = force_premium ?? post.force_premium;
  enforce_paywall_breaks = enforce_paywall_breaks ?? post.enforce_paywall_breaks;
  contains_subscriber_break = contains_subscriber_break ?? post.contains_subscriber_break;
  const currentSubscription = subscription || previewSubscription;

  const { cookies, isLoggedIn: isLoggedInOutletValue, admin } = useOutletContext<OutletContext>();
  const { isFetchingSubscription } = useRouteLoaderData('root') as RootLoaderData;

  // Determine logged in status for preview
  const hasJwtPreviewSubscription = isDraft && !!previewSubscription;
  const isLoggedIn = isLoggedInOutletValue || (!!currentSubscription && isPreviewModal) || hasJwtPreviewSubscription;

  const isPopupDisabled = isFetchingSubscription || Boolean(cookies.popup_closed) || isLoggedIn;
  const isSimulatingNonSubscriber = searchParams.getAll('tier_ids[]').includes('none');
  const isGatedContentDisabled =
    Boolean(cookies.email_capture_disabled) || isLoggedIn || (isDraft && !isSimulatingNonSubscriber);
  const isPostPremium = post.audience === 'premium';
  const isAdmin = Boolean(admin);
  const isSubscriptionPremium = currentSubscription?.tier === 'premium' && currentSubscription?.status !== 'paused';
  const hidePremiumComments = Boolean(isPostPremium && !isSubscriptionPremium && !isAdmin && !isDraft);
  const canShowCommentsNode = commentsEnabled && !hidePremiumComments;

  // Wait for the personalized meta fetch before choosing a comment system, so native
  // comments don't flash before the community thread swaps in. community_comments is one object, never a list.
  const publicationHasCommunity = Boolean(site.publication.community_enabled && site.publication.has_community);
  const awaitingCommunityMeta = publicationHasCommunity && !meta_information_fetched;
  const showCommunityComments = canShowCommentsNode && !awaitingCommunityMeta && community_comments != null;
  const showNativeComments = canShowCommentsNode && !awaitingCommunityMeta && community_comments == null;

  const showSubscriberBreak = contains_subscriber_break && !isGatedContentDisabled;
  const showPaywallBreak = !!paywall_shown;
  const showPaywallAndHidePostContent = hide_premium_post;
  const showMeteredUsageBanner = !!metered_usage_shown;

  const [isMeteredPaywallModalOpen, setIsMeteredPaywallModalOpen] = useState(false);

  // Advanced Email Capture
  const isSubscriberGateEnabled =
    !isMeteredPaywallModalOpen &&
    post.enforce_gated_content === true &&
    !showSubscriberBreak &&
    !isGatedContentDisabled &&
    !isFetchingSubscription;
  const isEmailCapturePopupEnabled =
    !isMeteredPaywallModalOpen &&
    post.enable_popup_on_scroll === true &&
    !showSubscriberBreak &&
    !isGatedContentDisabled &&
    !isFetchingSubscription;

  // Recommended Reading State
  const showRecommendedPostsNode = !isDraft && !isSubscriberGateEnabled && !onlyRenderComments;

  // Audio block (text to speech) - only available for posts, not templates
  const showTextToSpeechBlock =
    'text_to_speech_config' in post &&
    !!post.text_to_speech_config?.audio_url &&
    !isSubscriberGateEnabled &&
    !showSubscriberBreak &&
    !showPaywallBreak &&
    !showPaywallAndHidePostContent;

  const paywallUrl = useMemo(() => {
    const _searchParams = new URLSearchParams(location.search);

    if (paywall && 'offer_id' in paywall && paywall.offer_id) {
      _searchParams.set('offer_id', paywall.offer_id);
    }
    const search = _searchParams.toString();
    return `/upgrade${search ? '?' : ''}${search}`;
  }, [paywall, location.search]);

  return (
    <PostPageContext.Provider
      value={{
        postId: post.id,
        canShowCommentsNode,
        communityComments: community_comments,
        showCommunityComments,
        showNativeComments,
        isGatedContentDisabled,
        onlyRenderComments,
        isPopupDisabled,
        hidePremiumComments,
        showRecommendedPostsNode,
        isSubscriberGateEnabled,
        isEmailCapturePopupEnabled,
        showSubscriberBreak,
        showPaywallBreak,
        showPaywallAndHidePostContent,
        showMeteredUsageBanner,
        isMeteredPaywallModalOpen,
        setIsMeteredPaywallModalOpen,
        paywallUrl,
        showTextToSpeechBlock,
        utmParams: {
          utmParamsEnabled: post.utm_params_enabled,
          utmCampaign: post.utm_campaign ?? undefined,
          utmMedium: post.utm_medium ?? undefined,
          utmSource: post.utm_source ?? undefined,
        },
        forcePremium: force_premium,
        enforcePaywallBreaks: enforce_paywall_breaks ?? false,
      }}
    >
      {children}
    </PostPageContext.Provider>
  );
};
